import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_USERS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";

// Create context
const UseWebshopUsers = createContext();

// Provider component
export const WebshopUsersProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [webshopUsers, setWebshopUsers] = useState([]);
  const [error, setError] = useState(null);
  const [listener, setListener] = useState(null);

  // Set up listener when the provider mounts
  useEffect(() => {
    setupListener();

    // Cleanup listener when unmounting
    return () => {
      if (listener) {
        listener();
      }
    };
  }, []);

  const setupListener = () => {
    setIsLoading(true);
    setError(null);

    try {
      const usersQuery = query(
        collection(firestore, FIRESTORE_USERS_TABLE),
        where("userRole", "==", "user"),
        where("userRegion", "==", COMPANY_COUNTRY_CODE),
      );

      const unsubscribe = onSnapshot(
        usersQuery,
        (snapshot) => {
          try {
            const usersData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setWebshopUsers(usersData);
            setError(null);
            setIsLoading(false);
          } catch (err) {
            console.error("Error processing webshop users snapshot:", err);
            setError(err.message);
            setIsLoading(false);
            message.error("Failed to process webshop users data");
          }
        },
        (error) => {
          console.error("Error listening to webshop users:", error);
          setError(error.message);
          setIsLoading(false);
          message.error("Failed to fetch webshop users data");
        },
      );

      setListener(() => unsubscribe);
    } catch (error) {
      console.error("Error setting up webshop users listener:", error);
      setError(error.message);
      setIsLoading(false);
      message.error("Failed to setup webshop users listener");
    }
  };

  // Helper functions
  const getUserById = (id) => {
    return webshopUsers.find((user) => user.id === id || user.userId === id);
  };

  const getUsersByRegion = (region) => {
    return webshopUsers.filter((user) => user.userRegion === region);
  };

  const searchUsers = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return webshopUsers.filter(
      (user) => user.userName?.toLowerCase().includes(term) || user.userEmail?.toLowerCase().includes(term),
    );
  };

  return (
    <UseWebshopUsers.Provider
      value={{
        isLoading,
        webshopUsers,
        error,
        getUserById,
        getUsersByRegion,
        searchUsers,
      }}
    >
      {children}
    </UseWebshopUsers.Provider>
  );
};

// Custom hook to use the webshop users context
export const useWebshopUsers = () => {
  const context = useContext(UseWebshopUsers);

  if (context === undefined) {
    throw new Error("useWebshopUsers must be used within a WebshopUsersProvider");
  }

  return context;
};
